import { useState } from "react";
import axios from "axios";
import { Sparkles, Printer, PenLine, GraduationCap, ArrowLeft, Loader2, RefreshCw, Check, BookOpen, Mail } from "lucide-react";
import { Toaster, toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADES = ["Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];

const CARD_TINTS = ["tint-1", "tint-2", "tint-3", "tint-4", "tint-5"];

function InputScreen({ onGenerate, loading }) {
  const [interests, setInterests] = useState("");
  const [grade, setGrade] = useState("");
  const [touched, setTouched] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!interests.trim() || !grade) {
      toast.error("Tell us what they love and pick a grade — we'll do the rest.");
      return;
    }
    onGenerate({ interests: interests.trim(), grade_level: grade });
  };

  return (
    <section className="animate-fade-up" data-testid="screen-input">
      <div className="mb-10 md:mb-14">
        <div className="inline-flex items-center gap-2 mb-5">
          <Sparkles className="h-5 w-5 text-terracotta" strokeWidth={2.25} />
          <span className="font-handwriting text-2xl text-terracotta -rotate-2">the spark</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
          Turn your kid&apos;s interests into writing they might actually <span className="italic">want</span> to do.
        </h1>
        <p className="mt-5 text-base md:text-lg text-ink-soft max-w-xl leading-relaxed">
          Five prompts. One formula. Zero blank pages.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white card-edge rounded-3xl p-6 md:p-10 space-y-7"
        data-testid="form-generate"
      >
        <div className="space-y-3">
          <Label htmlFor="interests" className="flex items-center gap-2 text-ink font-medium text-base">
            <PenLine className="h-4 w-4 text-terracotta" />
            What does your kid love?
          </Label>
          <Input
            id="interests"
            data-testid="input-kid-interest"
            value={interests}
            onChange={(e) => setInterests(e.target.value)}
            placeholder="Type anything — sports, animals, games, books, whatever they're into."
            className="h-14 text-base md:text-lg rounded-xl border-2 border-stone-200 bg-paper focus-visible:ring-terracotta/30 focus-visible:border-terracotta placeholder:text-ink-muted"
            aria-invalid={touched && !interests.trim()}
          />
        </div>

        <div className="space-y-3">
          <Label htmlFor="grade" className="flex items-center gap-2 text-ink font-medium text-base">
            <GraduationCap className="h-4 w-4 text-terracotta" />
            Grade level
          </Label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger
              id="grade"
              data-testid="select-grade"
              className="h-14 text-base md:text-lg rounded-xl border-2 border-stone-200 bg-paper focus:ring-terracotta/30 focus:border-terracotta data-[placeholder]:text-ink-muted"
            >
              <SelectValue placeholder="Pick a grade" />
            </SelectTrigger>
            <SelectContent>
              {GRADES.map((g) => (
                <SelectItem key={g} value={g} data-testid={`grade-${g.replace(" ", "-").toLowerCase()}`}>
                  {g}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="btn-generate"
          className="w-full sm:w-auto h-14 px-8 rounded-full text-lg font-semibold bg-terracotta hover:bg-terracotta-hover text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-70 disabled:translate-y-0"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Gathering sparks…
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-5 w-5" /> Generate My Sparks
            </>
          )}
        </Button>
      </form>

      <p className="mt-6 text-sm text-ink-muted">
        No login. No data stored. Just five custom prompts in a few seconds.
      </p>
    </section>
  );
}

function LoadingScreen() {
  return (
    <div
      className="flex flex-col items-center justify-center py-24 text-center animate-fade-up"
      data-testid="loading-state"
    >
      <Sparkles className="h-12 w-12 text-terracotta animate-spark-pulse" strokeWidth={2} />
      <p className="mt-6 font-handwriting text-3xl text-terracotta -rotate-2">
        Gathering some sparks…
      </p>
      <p className="mt-2 text-ink-soft">Customizing every prompt around what your kid loves.</p>
    </div>
  );
}


function PromptCard({ prompt, index }) {
  return (
    <article
      className={`${CARD_TINTS[index]} card-edge print-card rounded-2xl p-6 md:p-7 flex flex-col gap-4 animate-fade-up print-text-black`}
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`card-prompt-${index + 1}`}
    >
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <span className="spark-accent text-3xl -rotate-2 inline-block">Spark {prompt.spark_number}</span>
          <h3 className="font-serif text-xl md:text-2xl text-ink mt-1 leading-tight">
            {prompt.spark_type}
          </h3>
        </div>
      </header>

      <div className="space-y-4">
        <Field label="Topic" value={prompt.topic} />
        <Field label="Hook Starter" value={prompt.hook_starter} italic />
        <Field label="Thesis Stem" value={prompt.thesis_stem} italic />
        <Field label="Evidence Prompt" value={prompt.evidence_prompt} />
        <Field label="Closing" value={prompt.closing_stem} italic />
      </div>
    </article>
  );
}

function Field({ label, value, italic }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-muted mb-1">
        {label}
      </div>
      <p className={`text-[15px] md:text-base text-ink-soft leading-relaxed ${italic ? "font-serif italic" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function ResultsScreen({ data, onBack, onRegenerate, regenerating }) {
  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const buildPrintHTML = () => {
    const cards = data.prompts
      .map(
        (p) => `
        <article class="card">
          <div class="spark">Spark ${p.spark_number}</div>
          <h2>${escapeHtml(p.spark_type)}</h2>
          <div class="field"><div class="label">Topic</div><p>${escapeHtml(p.topic)}</p></div>
          <div class="field"><div class="label">Hook Starter</div><p class="italic">${escapeHtml(p.hook_starter)}</p></div>
          <div class="field"><div class="label">Thesis Stem</div><p class="italic">${escapeHtml(p.thesis_stem)}</p></div>
          <div class="field"><div class="label">Evidence Prompt</div><p>${escapeHtml(p.evidence_prompt)}</p></div>
          <div class="field"><div class="label">Closing</div><p class="italic">${escapeHtml(p.closing_stem)}</p></div>
        </article>`
      )
      .join("");

    return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>The Spark — ${escapeHtml(data.interests)} (${escapeHtml(data.grade_level)})</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Outfit:wght@300;400;500;600;700&family=Caveat:wght@400..700&display=swap" rel="stylesheet" />
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: 'Outfit', system-ui, sans-serif; }
    .page { max-width: 760px; margin: 0 auto; padding: 32px 28px 48px; }
    header { margin-bottom: 18px; }
    .kicker { font-family: 'Caveat', cursive; font-size: 26px; color: #E07A5F; transform: rotate(-2deg); display: inline-block; }
    h1 { font-family: 'Lora', serif; font-size: 30px; line-height: 1.15; margin: 6px 0 4px; }
    .meta { font-size: 13px; color: #5C554D; margin-bottom: 14px; }
    .intro { font-size: 14px; color: #3A3530; line-height: 1.5; border: 1.5px solid #000; border-radius: 8px; padding: 12px 14px; margin-bottom: 18px; }
    .intro em { font-family: 'Lora', serif; font-style: italic; }
    .card { border: 1.5px solid #000; border-radius: 10px; padding: 16px 18px; margin-bottom: 14px; page-break-inside: avoid; break-inside: avoid; }
    .spark { font-family: 'Caveat', cursive; font-size: 22px; color: #E07A5F; line-height: 1; }
    h2 { font-family: 'Lora', serif; font-size: 20px; margin: 2px 0 12px; }
    .field { margin-top: 10px; }
    .label { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 600; color: #5C554D; margin-bottom: 3px; }
    .field p { margin: 0; font-size: 13.5px; line-height: 1.55; color: #1f1b18; }
    .italic { font-family: 'Lora', serif; font-style: italic; }
    .footer { margin-top: 22px; font-size: 12.5px; color: #3A3530; border-top: 1px dashed #888; padding-top: 12px; }
    .footer a { color: #E07A5F; font-weight: 600; text-decoration: underline; }
    .toolbar { position: fixed; top: 14px; right: 14px; display: flex; gap: 8px; }
    .toolbar button { font: 600 13px 'Outfit', sans-serif; background: #E07A5F; color: #fff; border: 0; border-radius: 999px; padding: 9px 16px; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,.15); }
    .toolbar button.secondary { background: #fff; color: #3A3530; border: 1.5px solid #ddd; }
    @media print {
      .toolbar { display: none; }
      .page { padding: 0 14px; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="secondary" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print / Save PDF</button>
  </div>
  <div class="page">
    <header>
      <span class="kicker">the spark</span>
      <h1>Five sparks for ${escapeHtml(data.interests)}</h1>
      <div class="meta">Built for ${escapeHtml(data.grade_level)}</div>
    </header>
    <div class="intro">
      These 5 prompts cover different types of writing — but notice they all follow the same structure.
      <em>Hook. Thesis. Evidence. Closing.</em> That's the formula that works for everything.
    </div>
    ${cards}
    <div class="footer">
      Learn how to use AI prompts to document homeschool in 10 minutes a day — step by step, no experience needed.
      Visit <a href="https://10minuterecords.com">10minuterecords.com</a>
    </div>
  </div>
  <script>
    (function () {
      var fired = false;
      function doPrint() {
        if (fired) return;
        fired = true;
        try { window.focus(); window.print(); } catch (e) {}
      }
      function ready() {
        // Wait for web fonts so the printed layout uses the right typography
        var fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
        fontsReady.then(function () { setTimeout(doPrint, 250); }, function () { setTimeout(doPrint, 250); });
        // Safety net: print even if fonts.ready never resolves
        setTimeout(doPrint, 1800);
      }
      if (document.readyState === 'complete') ready();
      else window.addEventListener('load', ready);
    })();
  </script>
</body>
</html>`;
  };

  const handlePrint = () => {
    const html = buildPrintHTML();
    try {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      // Anchor-click is treated as user-initiated navigation — not blocked by popup blockers
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      // Fallback: inline-print the current page (works when not embedded)
      try { window.print(); } catch (_) {
        toast.error("Couldn't open the printable view. Try again from a non-embedded page.");
      }
    }
  };

  return (
    <section className="animate-fade-up" data-testid="screen-results">
      <div className="no-print flex items-center justify-between mb-8 gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-ink-soft hover:text-terracotta transition-colors text-sm font-medium"
          data-testid="btn-back"
        >
          <ArrowLeft className="h-4 w-4" /> start over
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={onRegenerate}
            disabled={regenerating}
            data-testid="btn-regenerate"
            variant="outline"
            className="rounded-full border-2 border-ink/15 hover:border-terracotta hover:text-terracotta bg-white disabled:opacity-60"
          >
            {regenerating ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating…</>
            ) : (
              <><RefreshCw className="mr-2 h-4 w-4" /> Regenerate</>
            )}
          </Button>
          <Button
            onClick={handlePrint}
            data-testid="btn-print"
            variant="outline"
            className="rounded-full border-2 border-ink/15 hover:border-terracotta hover:text-terracotta bg-white"
          >
            <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      <header className="mb-10">
        <div className="inline-flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-terracotta" />
          <span className="font-handwriting text-2xl text-terracotta -rotate-2">your five sparks</span>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.1] tracking-tight">
          Here are five sparks. Pick one and watch what happens.
        </h2>
        <div className="mt-6 bg-white/70 card-edge rounded-2xl p-5 md:p-6 print-card">
          <p className="text-ink-soft text-base md:text-lg leading-relaxed">
            These 5 prompts cover different types of writing — but notice they all follow the
            same structure.{" "}
            <span className="font-serif italic text-ink">Hook. Thesis. Evidence. Closing.</span>{" "}
            That&apos;s the formula that works for everything.
          </p>
          <p className="mt-3 text-sm text-ink-muted">
            Built for <span className="font-medium text-ink-soft">{data.grade_level}</span> · around{" "}
            <span className="font-medium text-ink-soft">{data.interests}</span>
          </p>
        </div>
      </header>

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 transition-opacity duration-300 ${regenerating ? "opacity-40 pointer-events-none" : "opacity-100"}`}>
        {data.prompts.map((p, i) => (
          <PromptCard key={`${data.interests}-${p.spark_number}-${i}`} prompt={p} index={i} />
        ))}
      </div>

      {/* Email signup — right after the value, before the upsell */}
      <section
        className="no-print mt-12 bg-white card-edge rounded-3xl p-7 md:p-10 relative overflow-hidden"
        data-testid="email-signup-section"
      >
        <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-terracotta/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-3">
            <Mail className="h-5 w-5 text-terracotta" strokeWidth={2.25} />
            <span className="font-handwriting text-2xl text-terracotta -rotate-2">stay sparked</span>
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-ink leading-snug">
            Want more prompts like these, straight to your inbox?
          </h3>
          <p className="mt-2 text-ink-soft text-base md:text-lg max-w-xl">
            Drop your email and we&apos;ll send the occasional writing tip your kid will actually use — nothing else.
          </p>

          {/* ============================================================
              THIRD-PARTY EMAIL FORM — wealthery / GHL inline form
              ============================================================ */}
          <div
            id="third-party-email-form"
            data-testid="third-party-email-form"
            className="mt-6 max-w-xl"
          >
            <iframe
              src="https://links.wealthery.com/widget/form/OpAOfdn7m5fIMbs2GkRx"
              style={{ display: "none", width: "100%", height: "100%", border: "none", borderRadius: "8px" }}
              id="inline-OpAOfdn7m5fIMbs2GkRx"
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="leadCollected"
              data-deactivation-value=""
              data-form-name="The Spark — Email Capture"
              data-height="undefined"
              data-layout-iframe-id="inline-OpAOfdn7m5fIMbs2GkRx"
              data-form-id="OpAOfdn7m5fIMbs2GkRx"
              title="The Spark — Email Capture"
            />
          </div>
          {/* ============================================================
              END THIRD-PARTY EMAIL FORM
              ============================================================ */}

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-terracotta" /> No spam, ever
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-terracotta" /> Unsubscribe anytime
            </span>
          </div>
        </div>
      </section>

      {/* Upsell card — most receptive moment, right after they see personalized prompts */}
      <aside
        className="no-print mt-12 relative overflow-hidden bg-white card-edge rounded-3xl p-7 md:p-10"
        data-testid="upsell-card"
      >
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-terracotta/10 blur-2xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row gap-6 items-start sm:items-center">
          <div className="shrink-0 w-14 h-14 rounded-2xl bg-terracotta/15 flex items-center justify-center">
            <BookOpen className="h-7 w-7 text-terracotta" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-handwriting text-xl text-terracotta -rotate-2 inline-block">for parents</span>
            <p className="mt-1 font-serif text-xl md:text-2xl text-ink leading-snug">
              Most kids hate writing. Not because they are lazy — because nobody gave them the formula. The Young Author Writing Workshop gives your middle schooler the exact essay formula before high school arrives and the stakes get real.
            </p>
            <a
              href="https://10minuterecords.com/young-author-summer"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="link-10minuterecords"
              className="mt-4 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-semibold shadow-md transition-all hover:-translate-y-0.5"
            >
              See the Workshop →
            </a>
          </div>
        </div>
      </aside>
    </section>
  );
}

export default function App() {
  const [step, setStep] = useState("input"); // input | loading | results
  const [data, setData] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const generate = async (payload) => {
    setStep("loading");
    try {
      const res = await axios.post(`${API}/sparks/generate`, payload);
      setData(res.data);
      setStep("results");
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Couldn't reach the sparks just now. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
      setStep("input");
    }
  };

  const regenerate = async () => {
    if (!data || regenerating) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/sparks/generate`, {
        interests: data.interests,
        grade_level: data.grade_level,
      });
      setData(res.data);
      toast.success("Fresh sparks, same kid.");
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Couldn't regenerate just now. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setRegenerating(false);
    }
  };

  const startOver = () => {
    setData(null);
    setStep("input");
  };

  return (
    <div className="paper-grain min-h-screen">
      <Toaster position="top-center" richColors />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 md:py-16">
        {step === "input" && <InputScreen onGenerate={generate} loading={false} />}
        {step === "loading" && <LoadingScreen />}
        {step === "results" && data && (
          <ResultsScreen
            data={data}
            onBack={startOver}
            onRegenerate={regenerate}
            regenerating={regenerating}
          />
        )}
      </main>
    </div>
  );
}
